import { envConfig } from "@/config";
import { posthog } from "posthog-js";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

export const initPostHog = (username: string, email: string, applianceId: string, applianceName: string): void => {
	if (!posthog.__loaded) {
		posthog.init(envConfig.POSTHOG_KEY || "", {
			api_host: envConfig.POSTHOG_HOST || "https://app.posthog.com",
			autocapture: true, // Enable automatic event tracking
			capture_pageview: false, // Set to false to manually track page views
			capture_performance: true, // Enable performance tracking
			disable_session_recording: false, // Enable session recording
			session_recording: {
				recordCrossOriginIframes: true,
				maskAllInputs: false,
				maskInputOptions: {
					password: true,
				},
			},
			session_idle_timeout_seconds: 28800, // Session timeout set to 8 hrs
			rate_limiting: {
				events_per_second: 20,
				events_burst_limit: 200,
			},
		});
	}

	if (username && applianceId) {
		const postHogUserIdentifier = username + " + " + applianceId;
		posthog.identify(postHogUserIdentifier, {
			username,
			email,
			applianceId,
			applianceName,
		});
	}
};

export const usePosthogRouteChangeListener = (): void => {
	const location = useLocation();

	useEffect(() => {
		// Capture pageview event on route change
		posthog.capture("$pageview", { url: location.pathname });
	}, [location]);
};

export const resetPosthog = (): void => {
	posthog.reset();
};

/**
 * Captures a focus event via PostHog.
 *
 * @param isPreset - If true, captures a 'focus_preset' event; otherwise, captures a 'focus_applied' event.
 * @param isLocked - Whether the focus is locked (tabsLocked).
 * @param teacherId - The logged in teacher's unique identifier.
 * @param applianceId - The appliance/device unique identifier.
 */
export function captureFocusEvent(isPreset: boolean, isLocked: boolean, teacherId: string, applianceId: string): void {
	//capture it as preset event
	if (isPreset) {
		posthog.capture("focus_preset", {
			teacherId: teacherId,
			applianceId: applianceId,
			isLocked: isLocked,
		});
	} else {
		posthog.capture("focus_applied", {
			teacherId: teacherId,
			applianceId: applianceId,
			isLocked: isLocked,
		});
	}
}

/**
 * Converts TimeInput24Hr object to readable time string format.
 *
 * @param time - The time object with hour and minute properties.
 * @returns Formatted time string in HH:MM format.
 */
function formatTimeInput24Hr(time: { hour: number; minute: number }): string {
	return `${time.hour.toString().padStart(2, "0")}:${time.minute.toString().padStart(2, "0")}`;
}

/**
 * Captures a schedule event (create or update) via PostHog.
 *
 * @param isUpdate - If true, captures a 'schedule_updated' event; otherwise, captures a 'schedule_created' event.
 * @param day - The day of the week for the schedule.
 * @param startTime - The start time object of the schedule.
 * @param endTime - The end time object of the schedule.
 * @param endDate - The end date of the schedule.
 * @param active - Whether the schedule is active.
 * @param teacherUsername - The logged in teacher's username.
 * @param applianceId - The appliance/device unique identifier.
 * @param classroomName - The classroom display name.
 */
export function captureScheduleCreationOrUpdateEvent(
	isUpdate: boolean,
	day: string,
	startTime: { hour: number; minute: number },
	endTime: { hour: number; minute: number },
	endDate: Date,
	active: boolean,
	teacherUsername: string,
	applianceId: string,
	classroomName: string
): void {
	const eventName = isUpdate ? "class_schedule_updated" : "class_schedule_created";

	posthog.capture(eventName, {
		teacherId: teacherUsername,
		applianceId: applianceId,
		classroomName: classroomName,
		day: day,
		startTime: formatTimeInput24Hr(startTime),
		endTime: formatTimeInput24Hr(endTime),
		endDate: endDate.toISOString(),
		active: active,
	});
}

/**
 * Captures a schedule deletion event via PostHog.
 *
 * @param day - The day of the week for the deleted schedule.
 * @param startTime - The start time object of the deleted schedule.
 * @param endTime - The end time object of the deleted schedule.
 * @param endDate - The end date of the deleted schedule.
 * @param active - Whether the deleted schedule was active.
 * @param teacherUsername - The logged in teacher's username.
 * @param applianceId - The appliance/device unique identifier.
 * @param classroomName - The classroom display name.
 */
export function captureScheduleDeletionEvent(
	day: string,
	startTime: { hour: number; minute: number },
	endTime: { hour: number; minute: number },
	endDate: Date,
	active: boolean,
	teacherUsername: string,
	applianceId: string,
	classroomName: string
): void {
	posthog.capture("class_schedule_deleted", {
		teacherId: teacherUsername,
		applianceId: applianceId,
		classroomName: classroomName,
		day: day,
		startTime: formatTimeInput24Hr(startTime),
		endTime: formatTimeInput24Hr(endTime),
		endDate: endDate.toISOString(),
		active: active,
	});
}

/**
 * Captures a schedule duplicate event via PostHog.
 *
 * @param teacherUsername - The logged in teacher's username.
 * @param applianceId - The appliance/device unique identifier.
 * @param classroomName - The classroom display name.
 */
export function captureScheduleDuplicateEvent(teacherUsername: string, applianceId: string, classroomName: string): void {
	posthog.capture("class_schedule_duplicated", {
		teacherId: teacherUsername,
		applianceId: applianceId,
		classroomName: classroomName,
	});
}

/**
 * Captures a schedule toggle (activate/deactivate) event via PostHog.
 *
 * @param isActive - Whether the schedule is being activated (true) or deactivated (false).
 * @param day - The day of the week for the schedule.
 * @param startTime - The start time object of the schedule.
 * @param endTime - The end time object of the schedule.
 * @param endDate - The end date of the schedule.
 * @param teacherUsername - The logged in teacher's username.
 * @param applianceId - The appliance/device unique identifier.
 * @param classroomName - The classroom display name.
 */
export function captureScheduleToggleEvent(
	isActive: boolean,
	day: string,
	startTime: { hour: number; minute: number },
	endTime: { hour: number; minute: number },
	endDate: Date,
	teacherUsername: string,
	applianceId: string,
	classroomName: string
): void {
	const eventName = isActive ? "class_schedule_activated" : "class_schedule_deactivated";

	posthog.capture(eventName, {
		teacherId: teacherUsername,
		applianceId: applianceId,
		classroomName: classroomName,
		day: day,
		startTime: formatTimeInput24Hr(startTime),
		endTime: formatTimeInput24Hr(endTime),
		endDate: endDate.toISOString(),
		active: isActive,
	});
}

/**
 * Captures a tab limit enabled event via PostHog when a class has tab limit applied.
 *
 * @param teacherUsername - The logged in teacher's unique identifier (username).
 * @param applianceId - The appliance/device unique identifier.
 * @param classroomName - The classroom display name / source id.
 * @param tabLimitValue - The tab limit value that was applied.
 */
export function captureTabLimitEnabledEvent(
	teacherUsername: string,
	applianceId: string,
	classroomName: string,
	tabLimitValue: number
): void {
	posthog.capture("tab_limit_enabled", {
		teacherId: teacherUsername,
		applianceId: applianceId,
		classroomName: classroomName,
		timestamp: new Date().toISOString(),
		tabLimitValue: tabLimitValue,
	});
}

/**
 * Captures a tab limit adjusted event via PostHog when tab limits are edited.
 *
 * @param teacherUsername - The logged in teacher's unique identifier (username).
 * @param applianceId - The appliance/device unique identifier.
 * @param classroomName - The classroom display name / source id.
 * @param tabLimitOldValue - The tab limit value before the edit (null if none was set).
 * @param tabLimitNewValue - The tab limit value after the edit.
 */
export function captureTabLimitAdjustedEvent(
	teacherUsername: string,
	applianceId: string,
	classroomName: string,
	tabLimitOldValue: number | null,
	tabLimitNewValue: number
): void {
	posthog.capture("tab_limit_adjusted", {
		teacherId: teacherUsername,
		applianceId: applianceId,
		classroomName: classroomName,
		timestamp: new Date().toISOString(),
		tabLimitOldValue: tabLimitOldValue,
		tabLimitNewValue: tabLimitNewValue,
	});
}

/**
 * Captures a tab limit removed event via PostHog when tab limits are removed (e.g. via TabLimit pill).
 *
 * @param teacherUsername - The logged in teacher's unique identifier (username).
 * @param applianceId - The appliance/device unique identifier.
 * @param classroomName - The classroom display name / source id.
 */
export function captureTabLimitRemovedEvent(teacherUsername: string, applianceId: string, classroomName: string): void {
	posthog.capture("tab_limit_removed", {
		teacherId: teacherUsername,
		applianceId: applianceId,
		classroomName: classroomName,
		timestamp: new Date().toISOString(),
	});
}

export type StudentConflictDetectedEventParams = {
	applianceId: string;
	studentUsername: string;
	managingTeacherUsername: string;
	managingTeacherName: string;
	managingClassName: string;
	loggedInTeacherUsername: string;
	classroomName: string;
};

/**
 * Captures when a student conflict is detected (e.g. same student managed by another teacher/classroom).
 */
export function captureStudentConflictDetectedEvent(params: StudentConflictDetectedEventParams): void {
	posthog.capture("student_conflict_detected", {
		applianceId: params.applianceId,
		studentUsername: params.studentUsername,
		managingTeacherUsername: params.managingTeacherUsername,
		managingTeacherName: params.managingTeacherName,
		managingClassName: params.managingClassName,
		teacherId: params.loggedInTeacherUsername,
		classroomName: params.classroomName,
		timestamp: new Date().toISOString(),
	});
}

export type StudentClaimConfirmedEventParams = {
	applianceId: string;
	studentUsername: string;
	managementClaimedFromTeacher: string;
	loggedInTeacherUsername: string;
	managementClaimedByClassroom: string;
	managementClaimedFromClassroom: string;
};

/**
 * Captures when a student management claim is confirmed/success (e.g. after resolving a conflict).
 */
export function captureStudentClaimConfirmedEvent(params: StudentClaimConfirmedEventParams): void {
	posthog.capture("student_claim_confirmed", {
		applianceId: params.applianceId,
		studentUsername: params.studentUsername,
		managementClaimedByTeacher: params.loggedInTeacherUsername,
		managementClaimedByClassroom: params.managementClaimedByClassroom,
		managementClaimedFromTeacher: params.managementClaimedFromTeacher,
		managementClaimedFromClassroom: params.managementClaimedFromClassroom,
		timestamp: new Date().toISOString(),
	});
}

export type AccountSettingsSavedEventParams = {
	teacherUsername: string;
	applianceId: string;
	use24Hour: boolean;
	preferredLocale: string;
};

/**
 * Captures when account settings are successfully saved from My Account Settings.
 */
export function captureAccountSettingsSavedEvent(params: AccountSettingsSavedEventParams): void {
	posthog.capture("account_settings_saved", {
		teacherId: params.teacherUsername,
		applianceId: params.applianceId,
		use24Hour: params.use24Hour,
		preferredLocale: params.preferredLocale,
		timestamp: new Date().toISOString(),
	});
}

/** Tool clicked in the seat plan floating toolbar (Focus, Pause, Announcement, Open Tab, Screen Share). */
export type SeatPlanToolbarAction = "focus" | "pause" | "announcement" | "open_tab" | "screenshare";

/**
 * Captures when a teacher clicks a tool in the seat plan floating selection toolbar.
 * Fires on the top-level click (menu open or direct action), not on individual submenu items.
 *
 * @param teacherUsername - The logged in teacher's username.
 * @param applianceId - The appliance/device unique identifier.
 * @param classroomName - The classroom display name.
 * @param action - Which toolbar tool was clicked.
 * @param selectedCount - The number of students selected when the tool was clicked.
 */
export function captureSeatPlanToolbarActionClickedEvent(
	teacherUsername: string,
	applianceId: string,
	classroomName: string,
	action: SeatPlanToolbarAction,
	selectedCount: number
): void {
	posthog.capture("seat_plan_toolbar_action_clicked", {
		teacherId: teacherUsername,
		applianceId: applianceId,
		classroomName: classroomName,
		action: action,
		selectedCount: selectedCount,
		timestamp: new Date().toISOString(),
	});
}
